import React, { useEffect, useMemo, useState } from 'react';
import './previaArquivo.css';

// Corpo do modal "Ver" de Acesso a Arquivos. `preview.previa` vem de BaixaArquivoSamba (modo "previa", backend/src/services/previaArquivo.js):
// imagem/pdf/video/audio/fonte chegam com o base64 e o navegador abre; texto, planilha, Word, PowerPoint, compactado, e-mail e binario chegam
// ja convertidos em JSON. Tudo e desenhado com React (o texto do arquivo nunca vira HTML); so o .html "Pagina" vai num iframe sem scripts.

export const fmtTam = (b) => {
    if (!b) return '—';
    const u = ['B', 'KB', 'MB', 'GB', 'TB'];
    let i = 0, n = b;
    while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
    return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
};

function bytesDe(b64) {
    const bin = atob(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return arr;
}
// blob: em vez de data: (o Chrome nao abre PDF em data: dentro de iframe); revoga ao fechar.
function useBlobUrl(base64, mime) {
    const [url, setUrl] = useState('');
    useEffect(() => {
        if (!base64) { setUrl(''); return undefined; }
        const u = URL.createObjectURL(new Blob([bytesDe(base64)], { type: mime || 'application/octet-stream' }));
        setUrl(u);
        return () => URL.revokeObjectURL(u);
    }, [base64, mime]);
    return url;
}
const letra = (i) => { let s = ''; for (let n = i + 1; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s; return s; };

const Aviso = ({ children }) => <div className="pv-aviso">{children}</div>;
const Rodape = ({ children }) => <div className="pv-rodape">{children}</div>;
const plural = (n, um, varios) => `${n.toLocaleString('pt-BR')} ${n === 1 ? um : varios}`;
// imagens de Word/PowerPoint que nao couberam (acima do limite ou em formato que o navegador nao abre): nada some em silencio
const Omitidas = ({ n }) => (n > 0 ? <Aviso>{plural(n, 'imagem não foi exibida', 'imagens não foram exibidas')} (grande demais ou em formato que o navegador não abre). Use Baixar para ver tudo.</Aviso> : null);

function Imagem({ p, nome }) {
    const url = useBlobUrl(p.base64, p.mime);
    const [cheia, setCheia] = useState(false);
    const [erro, setErro] = useState(false);
    if (erro) return <Aviso>O navegador não conseguiu desenhar esta imagem. Use Baixar.</Aviso>;
    return url ? (
        <div className={`pv-imagem${cheia ? ' cheia' : ''}`}>
            <img src={url} alt={nome} onClick={() => setCheia((v) => !v)} onError={() => setErro(true)} title="Clique para alternar entre ajustar à tela e tamanho real" />
        </div>
    ) : null;
}
function Pdf({ p, nome }) {
    const url = useBlobUrl(p.base64, p.mime);
    return url ? <iframe title={nome} src={url} className="pv-frame" /> : null;
}
function Midia({ p, tag }) {
    const url = useBlobUrl(p.base64, p.mime);
    const [erro, setErro] = useState(false);
    if (erro) return <Aviso>O navegador não consegue tocar este formato. Use Baixar.</Aviso>;
    if (!url) return null;
    return tag === 'video'
        ? <video className="pv-media" src={url} controls onError={() => setErro(true)} />
        : <audio className="pv-audio" src={url} controls onError={() => setErro(true)} />;
}
function Fonte({ p }) {
    const [ok, setOk] = useState(null);
    const familia = useMemo(() => `pv-fonte-${Math.random().toString(36).slice(2)}`, []);
    useEffect(() => {
        let vivo = true;
        let face = null;
        (async () => {
            try {
                face = new FontFace(familia, bytesDe(p.base64).buffer);
                await face.load();
                document.fonts.add(face);
                if (vivo) setOk(true);
            } catch (_) { if (vivo) setOk(false); }
        })();
        return () => { vivo = false; if (face) document.fonts.delete(face); };
    }, [p.base64, familia]);
    if (ok === false) return <Aviso>Não consegui carregar esta fonte. Use Baixar.</Aviso>;
    if (!ok) return <div className="muted">Carregando…</div>;
    const amostra = { fontFamily: `"${familia}", sans-serif` };
    return (
        <div className="pv-fonte-amostra" style={amostra}>
            <div style={{ fontSize: 44 }}>Aa Bb Cc Dd Ee Ff Gg</div>
            <div style={{ fontSize: 28 }}>ABCDEFGHIJKLMNOPQRSTUVWXYZ</div>
            <div style={{ fontSize: 28 }}>abcdefghijklmnopqrstuvwxyz</div>
            <div style={{ fontSize: 28 }}>0123456789 ÁÉÍÓÚ ÀÂÃÕ Ç &amp; @ # R$</div>
            <div style={{ fontSize: 16 }}>Moda Babita: a vida é feita de escolhas, e a sua começa aqui.</div>
        </div>
    );
}

function Texto({ p }) {
    const [pagina, setPagina] = useState(false);
    const linhas = useMemo(() => (p.texto || '').split('\n').length, [p.texto]);
    const nums = useMemo(() => Array.from({ length: linhas }, (_, i) => i + 1).join('\n'), [linhas]);
    if (!p.texto) return <Aviso>Este arquivo não tem texto.</Aviso>;
    return (
        <>
            {p.aviso && <Aviso>{p.aviso}</Aviso>}
            {p.html && (
                <div className="pv-barra">
                    <button className={`btn ghost sm${!pagina ? ' on' : ''}`} onClick={() => setPagina(false)}>Código</button>
                    <button className={`btn ghost sm${pagina ? ' on' : ''}`} onClick={() => setPagina(true)} title="Mostra a página sem executar scripts">Página</button>
                </div>
            )}
            {pagina
                ? <iframe title="Página" sandbox="" srcDoc={p.texto} className="pv-frame" />
                : <div className="pv-codigo mono"><pre className="pv-nums" aria-hidden="true">{nums}</pre><pre className="pv-fonte">{p.texto}</pre></div>}
            {p.truncado && <Aviso>Mostrando só o começo do arquivo ({fmtTam(p.tamanho)} no total). Use Baixar para ver tudo.</Aviso>}
            <Rodape>{p.codificacao} · {plural(linhas, 'linha', 'linhas')}</Rodape>
        </>
    );
}

function Tabela({ p }) {
    const [i, setI] = useState(0);
    const aba = p.abas[Math.min(i, p.abas.length - 1)];
    if (!aba) return <Aviso>Esta planilha não tem abas.</Aviso>;
    const cols = Array.from({ length: aba.colunas }, (_, c) => c);
    return (
        <>
            <div className="pv-abas">
                {p.abas.map((a, k) => <button key={k} className={`pv-aba${k === i ? ' on' : ''}`} onClick={() => setI(k)}>{a.nome}</button>)}
                {p.abasTotal > p.abas.length && <span className="muted">+{p.abasTotal - p.abas.length} abas não mostradas</span>}
            </div>
            {aba.linhas.length === 0 ? <Aviso>Aba vazia.</Aviso> : (
                <div className="pv-tabela-wrap">
                    <table className="pv-tabela">
                        <thead><tr><th />{cols.map((c) => <th key={c}>{letra(c)}</th>)}</tr></thead>
                        <tbody>
                            {aba.linhas.map((l, r) => (
                                <tr key={r}><th>{r + 1}</th>{cols.map((c) => <td key={c} title={l[c] || undefined}>{l[c] || ''}</td>)}</tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <Rodape>
                {aba.linhas.length.toLocaleString('pt-BR')} de {plural(aba.totalLinhas, 'linha', 'linhas')} · {aba.colunas.toLocaleString('pt-BR')} de {plural(aba.totalColunas, 'coluna', 'colunas')}
            </Rodape>
        </>
    );
}

function Runs({ runs }) {
    return runs.map((r, i) => {
        const c = [r.b && 'b', r.i && 'i', r.u && 'u', r.s && 's'].filter(Boolean).join(' ');
        return <span key={i} className={c || undefined}>{r.x}</span>;
    });
}
function Blocos({ blocos }) {
    return blocos.map((b, i) => {
        if (b.t === 'img') return <img key={i} className="pv-doc-img" src={b.src} alt="" />;
        if (b.t === 'tabela') {
            return (
                <table key={i} className="pv-doc-tab">
                    <tbody>{b.linhas.map((l, r) => <tr key={r}>{l.map((c, k) => <td key={k} colSpan={c.span || 1}><Blocos blocos={c.blocos} /></td>)}</tr>)}</tbody>
                </table>
            );
        }
        const T = /^h[1-6]$/.test(b.est) ? b.est : 'p';
        return <T key={i} className={`${b.est === 'li' ? 'pv-doc-li' : 'pv-doc-p'}${b.alinha ? ` pv-al-${b.alinha}` : ''}`}><Runs runs={b.runs} /></T>;
    });
}
function Documento({ p }) {
    if (!p.blocos.length) return <Aviso>Este documento não tem texto.</Aviso>;
    return (
        <>
            <div className="pv-doc"><Blocos blocos={p.blocos} /></div>
            <Omitidas n={p.imagensOmitidas} />
            {p.truncado && <Aviso>Documento grande: mostrando só o começo. Use Baixar para ver tudo.</Aviso>}
            <Rodape>Visualização simplificada (texto, tabelas e imagens). Para a formatação original, use Baixar.</Rodape>
        </>
    );
}

function Slides({ p }) {
    return (
        <>
            {p.miniatura && <img className="pv-miniatura" src={p.miniatura} alt="Miniatura do primeiro slide" />}
            <div className="pv-slides">
                {p.slides.map((s) => {
                    const vazio = !s.titulo && !s.textos.length && !s.imagens.length && !s.tabelas.length;
                    return (
                        <div className="pv-slide" key={s.n}>
                            <span className="pv-slide-n">{s.n}</span>
                            {s.titulo && <h3 className="pv-slide-titulo">{s.titulo}</h3>}
                            {s.textos.map((t, i) => <p key={i}>{t}</p>)}
                            {s.tabelas.map((tab, k) => (
                                <table key={k} className="pv-doc-tab"><tbody>{tab.map((l, r) => <tr key={r}>{l.map((c, n) => <td key={n}>{c}</td>)}</tr>)}</tbody></table>
                            ))}
                            {s.imagens.length > 0 && <div className="pv-slide-imgs">{s.imagens.map((src, i) => <img key={i} src={src} alt="" />)}</div>}
                            {vazio && <span className="muted">(slide sem texto)</span>}
                        </div>
                    );
                })}
            </div>
            <Omitidas n={p.imagensOmitidas} />
            {p.truncado && <Aviso>Mostrando {p.slides.length} de {p.total} slides.</Aviso>}
            <Rodape>Visualização simplificada (textos, tabelas e imagens, sem o desenho do slide). Para o visual original, use Baixar.</Rodape>
        </>
    );
}

function Compactado({ p }) {
    return (
        <>
            <div className="pv-tabela-wrap">
                <table className="pv-tabela pv-zip">
                    <thead><tr><th>Nome</th><th>Tamanho</th><th>Compactado</th><th>Data</th></tr></thead>
                    <tbody>
                        {p.entradas.map((e, i) => (
                            <tr key={i}>
                                <td className="mono">{e.dir ? '📁 ' : ''}{e.nome}</td>
                                <td>{e.dir ? '—' : fmtTam(e.tamanho)}</td>
                                <td>{e.comprimido == null || e.dir ? '—' : fmtTam(e.comprimido)}</td>
                                <td>{e.data || '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {p.truncado && <Aviso>Mostrando {p.entradas.length.toLocaleString('pt-BR')} de {p.total.toLocaleString('pt-BR')} itens.</Aviso>}
            <Rodape>{p.total.toLocaleString('pt-BR')} itens. Para abrir os arquivos de dentro, use Baixar.</Rodape>
        </>
    );
}

function Email({ p }) {
    return (
        <>
            <dl className="pv-email">
                {Object.entries(p.cabecalhos).filter(([, v]) => v).map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
            </dl>
            {p.texto ? <div className="pv-doc"><p className="pv-doc-p">{p.texto}</p></div> : <Aviso>Este e-mail não tem texto.</Aviso>}
            {p.truncado && <Aviso>Mensagem longa: mostrando só o começo.</Aviso>}
            {p.anexos.length > 0 && (
                <>
                    <h4 className="pv-h4">Anexos ({p.anexos.length})</h4>
                    <ul className="pv-lista">{p.anexos.map((a, i) => <li key={i}><span className="mono">{a.nome}</span> <span className="muted">{a.mime} · {fmtTam(a.tamanho)}</span></li>)}</ul>
                </>
            )}
        </>
    );
}

function Binario({ p }) {
    return (
        <>
            {p.aviso && <Aviso>{p.aviso}</Aviso>}
            <Aviso>
                {p.formato ? `${p.formato}. ` : ''}Este tipo de arquivo não tem pré-visualização no navegador. Abaixo, os primeiros bytes{p.textos.length ? ' e os textos legíveis' : ''}. Use Baixar para abrir no programa certo.
            </Aviso>
            <pre className="pv-hex mono">{p.hex.map((l) => `${l.off}  ${l.hex.padEnd(47)}  ${l.asc}`).join('\n')}</pre>
            {p.textos.length > 0 && (
                <>
                    <h4 className="pv-h4">Textos encontrados no arquivo</h4>
                    <pre className="pv-hex mono">{p.textos.join('\n')}</pre>
                </>
            )}
        </>
    );
}

export default function PreviaArquivo({ preview }) {
    if (preview.carregando) return <div className="muted">Carregando…</div>;
    if (preview.grande) {
        return <Aviso>Este arquivo tem {fmtTam(preview.tamanho)}, acima do limite de {fmtTam(preview.limite)} para pré-visualizar. Use Baixar.</Aviso>;
    }
    const p = preview.previa;
    if (!p) return null;
    const corpo = (() => {
        switch (p.tipo) {
            case 'imagem': return <Imagem p={p} nome={preview.nome} />;
            case 'pdf': return <Pdf p={p} nome={preview.nome} />;
            case 'video': return <Midia p={p} tag="video" />;
            case 'audio': return <Midia p={p} tag="audio" />;
            case 'fonte': return <Fonte p={p} />;
            case 'texto': return <Texto p={p} />;
            case 'tabela': return <Tabela p={p} />;
            case 'documento': return <Documento p={p} />;
            case 'slides': return <Slides p={p} />;
            case 'compactado': return <Compactado p={p} />;
            case 'email': return <Email p={p} />;
            case 'binario': return <Binario p={p} />;
            case 'vazio': return <Aviso>Arquivo vazio.</Aviso>;
            default: return <Aviso>Tipo de pré-visualização desconhecido. Use Baixar.</Aviso>;
        }
    })();
    return (
        <>
            <div className="pv-topo">
                <span className="pv-rotulo">{p.rotulo}</span>
                <span>{fmtTam(preview.tamanho)}</span>
                {p.tipo === 'tabela' && <span>{p.abasTotal} {p.abasTotal === 1 ? 'aba' : 'abas'}</span>}
                {p.tipo === 'slides' && <span>{p.total} {p.total === 1 ? 'slide' : 'slides'}</span>}
            </div>
            {corpo}
        </>
    );
}
